import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import styles from './AuthCallback.module.css';

export function AuthCallback() {
  const navigate = useNavigate();
  const { user, needsUsername } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for error in URL
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const errorParam = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      // Check if we have a pending username from sign-up flow
      const pendingUsername = sessionStorage.getItem('pendingUsername');

      if (user) {
        if (pendingUsername && needsUsername) {
          // This was a sign-up with Apple - create the profile
          try {
            const { error: profileError } = await supabase
              .from('users')
              .insert({
                id: user.id,
                username: pendingUsername,
              });

            if (profileError) {
              throw profileError;
            }

            sessionStorage.removeItem('pendingUsername');
            // Profile created successfully, redirect to home
            // The auth state change will update the profile
            navigate('/');
          } catch (err) {
            console.error('Error creating profile:', err);
            setError('Failed to complete sign-up. Please try again.');
            sessionStorage.removeItem('pendingUsername');
            await supabase.auth.signOut();
            setTimeout(() => navigate('/'), 2000);
          }
        } else {
          // This was a sign-in - redirect home
          // If needsUsername is true, the UsernamePromptModal will show automatically
          navigate('/');
        }
      }
    };

    // Small delay to ensure auth state is populated
    const timeout = setTimeout(handleCallback, 500);

    return () => clearTimeout(timeout);
  }, [user, needsUsername, navigate]);

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.title}>Sign In Error</h2>
          <p className={styles.error}>{error}</p>
          <button className={styles.button} onClick={() => navigate('/')}>
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h2 className={styles.title}>Completing Sign In...</h2>
        <div className={styles.spinner}></div>
      </div>
    </div>
  );
}
